import { useCallback, useEffect, useRef } from 'react';
import { WidgetProps } from '../types/index.js';

interface UseWidgetDataOptions<T> {
  fetchData: () => Promise<T>;
  refreshInterval?: number;
  initialData?: T;
  dependencies?: any[];
}

interface UseWidgetDataReturn<T> {
  data: T | undefined;
  loading: boolean;
  error: string | undefined;
  refresh: () => Promise<void>;
}

/**
 * Hook for managing widget data fetching, loading states, and refresh intervals
 * Handles the common pattern of fetching data on mount and refreshing periodically
 */
export function useWidgetData<T>({
  widget,
  onUpdateState,
  fetchData,
  refreshInterval = 120000, // 2 minutes default
  initialData,
  dependencies = [],
}: UseWidgetDataOptions<T> & Pick<WidgetProps, 'widget' | 'onUpdateState'>): UseWidgetDataReturn<T> {
  const isFetchingRef = useRef(false);

  // Get data from widget state or use initial data
  const data = (widget.state.data as T) ?? initialData;
  const loading = widget.state.loading ?? false;
  const error = widget.state.error;

  const fetchDataInternal = useCallback(async () => {
    // Prevent multiple simultaneous fetches
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;
    onUpdateState({ loading: true, error: undefined });

    try {
      const newData = await fetchData();
      onUpdateState({ loading: false, data: newData, error: undefined });
    } catch (err) {
      console.error('Widget data fetch error:', err);
      onUpdateState({ 
        loading: false, 
        error: err instanceof Error ? err.message : 'Failed to fetch data' 
      });
    } finally {
      isFetchingRef.current = false;
    }
  }, [fetchData, onUpdateState]);

  // Initial fetch and refresh interval
  useEffect(() => {
    // Only fetch if we don't have data and not already loading
    if (!data && !loading) {
      fetchDataInternal();
    }

    // Set up refresh interval if specified
    let interval: ReturnType<typeof setInterval> | undefined;
    if (refreshInterval > 0) {
      interval = setInterval(fetchDataInternal, refreshInterval);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [fetchDataInternal, data, loading, refreshInterval]);

  // Re-fetch when dependencies change
  useEffect(() => {
    if (dependencies && dependencies.length > 0) {
      fetchDataInternal();
    }
  }, [...(dependencies || [])]);

  return {
    data,
    loading,
    error,
    refresh: fetchDataInternal,
  };
}